/* eslint-disable */
import React from "react";
import {
  Card,
  Text,
  Avatar,
  Stack,
  Divider,
  ScrollArea,
  createStyles,
  Center,
  Loader,
  Box,
} from "@mantine/core";
import { useGetNotifications } from "api/notifications/useNotifications";

const useStyles = createStyles((theme) => ({
  card: {
    borderRadius: 8,
    padding: 12,
    border: `1px solid ${theme.colors.gray[3]}`,
    background: theme.white,
    boxShadow: "0 6px 18px rgba(12,20,40,0.04)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "6px 8px",
  },
  title: {
    fontSize: 18,
    fontWeight: 800,
    color: theme.colors.dark[8],
  },
  list: {
    marginTop: 8,
  },
  row: {
    display: "flex",
    gap: 12,
    alignItems: "center",
    padding: "12px 6px",
  },
  textWrap: {
    minWidth: 0,
    flex: 1,
  },
  primaryText: {
    fontSize: 15,
    fontWeight: 700,
    color: theme.colors.dark[9],
    marginBottom: 6,
    wordBreak: "break-word",
  },
  secondaryText: {
    fontSize: 13,
    color: theme.colors.gray[6],
  },
}));

type NotificationItem = {
  id: string;
  description?: string;
  userName?: string;
  avatar?: string | null;
  createdAt?: string;
};

interface NotificationsCardProps {
  notifications?: NotificationItem[];
}

function getInitialsFromName(name?: string) {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function isUrl(value?: string | null) {
  if (!value) return false;
  return (
    /^(https?:)?\/\//.test(value) ||
    /\.(png|jpg|jpeg|svg|gif)(\?|$)/i.test(value)
  );
}

function extractNotificationsFromPage(page: unknown): NotificationItem[] {
  const p = page as any;
  if (Array.isArray(p)) return p;
  if (Array.isArray(p.notifications)) return p.notifications;
  if (Array.isArray(p.data)) return p.data;
  if (Array.isArray(p.items)) return p.items;
  if (Array.isArray(p.results)) return p.results;
  if (Array.isArray(p.payload)) return p.payload;
  if (p?.data && Array.isArray(p.data.notifications))
    return p.data.notifications;
  if (p?.notifications?.data && Array.isArray(p.notifications.data))
    return p.notifications.data;
  if (p && typeof p === "object") {
    for (const key of Object.keys(p)) {
      if (Array.isArray(p[key])) return p[key];
    }
  }
  return [];
}

export default function NotificationsCard({
  notifications,
}: NotificationsCardProps) {
  const { classes } = useStyles();
  const { data, isLoading, isError } = useGetNotifications();

  const apiList: NotificationItem[] = React.useMemo(() => {
    if (!data) return [];

    const pages = (data as any).pages as any[] | undefined;
    if (!pages || !Array.isArray(pages)) return [];

    return pages.flatMap((p) => extractNotificationsFromPage(p));
  }, [data]);

  const list =
    Array.isArray(notifications) && notifications.length
      ? notifications
      : apiList;

  // show only first 5 notifications
  const displayedList = React.useMemo(() => {
    if (!Array.isArray(list)) return [];
    return list.slice(0, 5);
  }, [list]);

  if (isLoading) {
    return (
      <Card className={classes.card} radius="md">
        <Center style={{ height: 180 }}>
          <Loader />
        </Center>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card className={classes.card} radius="md">
        <Box style={{ padding: 16 }}>
          <Text weight={700}>Unable to load notifications</Text>
        </Box>
      </Card>
    );
  }

  return (
    <Card className={classes.card} radius="md">
      <div className={classes.header}>
        <Text className={classes.title}>Notifications</Text>
      </div>

      <Divider />

      <ScrollArea style={{ height: 420, marginTop: 8 }} type="auto">
        <Stack spacing={0} className={classes.list}>
          {displayedList && displayedList.length ? (
            displayedList.map((n: NotificationItem) => {
              const avatarVal = n.avatar?.trim();
              const showAsImage = isUrl(avatarVal);
              const initials =
                avatarVal && !showAsImage
                  ? avatarVal
                  : getInitialsFromName(n.userName);

              return (
                <div key={n.id}>
                  <div className={classes.row}>
                    <Avatar
                      src={showAsImage ? avatarVal! : undefined}
                      radius="xl"
                      size={48}
                    >
                      {!showAsImage ? initials : null}
                    </Avatar>

                    <div className={classes.textWrap}>
                      <div>
                        <Text className={classes.primaryText}>
                          {n.description || "-"}
                        </Text>
                        <Text className={classes.secondaryText}>
                          {n.userName || "Unknown"}
                        </Text>
                      </div>
                    </div>
                  </div>

                  <Divider />
                </div>
              );
            })
          ) : (
            <Box style={{ padding: 16 }}>
              <Text>No notifications</Text>
            </Box>
          )}
        </Stack>
      </ScrollArea>
    </Card>
  );
}
